import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, MessageSquare, Bot, CreditCard, HelpCircle, LogOut, Menu, X, Package, FileText, Tag, History, Wifi, WifiOff, Settings, RefreshCw, Calendar, Link2, Megaphone, Shield, ChevronDown, ChevronRight } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [whatsappConnected, setWhatsappConnected] = useState(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [groups, setGroups] = useState([]);
  const [templatesExpanded, setTemplatesExpanded] = useState(false);

  useEffect(() => {
    // Verificar se é admin
    const checkAdmin = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await axios.get(`${API}/admin/check`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsAdmin(response.data.is_admin);
      } catch (error) {
        setIsAdmin(false);
      }
    };
    
    // Buscar grupos para o submenu de templates
    const fetchGroups = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await axios.get(`${API}/groups`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setGroups(response.data);
      } catch (error) {
        console.error('Erro ao carregar grupos:', error);
      }
    };
    
    checkAdmin();
    fetchGroups();
  }, []);

  useEffect(() => {
    // Verificar status do WhatsApp periodicamente (a cada 30 segundos)
    const checkWhatsAppStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await axios.get(`${API}/whatsapp/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWhatsappConnected(response.data.connected);
      } catch (error) {
        setWhatsappConnected(false);
      }
    };

    // Verificar status da assinatura
    const checkSubscriptionStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await axios.get(`${API}/auth/subscription-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubscriptionInfo(response.data);
      } catch (error) {
        console.error('Erro ao verificar assinatura:', error);
      }
    };

    checkWhatsAppStatus();
    checkSubscriptionStatus();
    const interval = setInterval(checkWhatsAppStatus, 30000);
    const subscriptionInterval = setInterval(checkSubscriptionStatus, 60000);
    return () => {
      clearInterval(interval);
      clearInterval(subscriptionInterval);
    };
  }, []);

  const handleRenewSubscription = () => {
    // Navigate to payment page
    navigate('/payment');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.clear();
    navigate('/login');
  };

  const menuItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', testId: 'nav-dashboard' },
    { path: '/clients', icon: Users, label: 'Clientes', testId: 'nav-clients' },
    { path: '/products', icon: Package, label: 'Produtos', testId: 'nav-products' },
    { path: '/templates', icon: FileText, label: 'Templates', testId: 'nav-templates', hasSubmenu: true },
    { path: '/groups', icon: Tag, label: 'Grupos', testId: 'nav-groups' },
    { path: '/meus-links', icon: Link2, label: 'Meus Links', testId: 'nav-meus-links' },
    { path: '/marketing', icon: Megaphone, label: 'Marketing', testId: 'nav-marketing' },
    { path: '/automation', icon: Bot, label: 'Automação', testId: 'nav-automation' },
    { path: '/whatsapp', icon: MessageSquare, label: 'WhatsApp', testId: 'nav-whatsapp' },
    { path: '/history', icon: History, label: 'Histórico', testId: 'nav-history' },
    { path: '/settings', icon: Settings, label: 'Configurações', testId: 'nav-settings' },
    { path: '/plans', icon: CreditCard, label: 'Planos', testId: 'nav-plans' },
    { path: '/support', icon: HelpCircle, label: 'Suporte', testId: 'nav-support' },
    ...(isAdmin ? [{ path: '/admin', icon: Shield, label: 'Admin', testId: 'nav-admin', isAdmin: true }] : []),
  ];

  const handleNavigate = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleTemplateGroupClick = (groupId) => {
    navigate(`/templates?group=${groupId}`);
    setMobileOpen(false);
  };

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white"
        data-testid="mobile-menu-btn"
      >
        {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed left-0 top-0 z-40 h-screen w-64 border-r border-zinc-800 bg-zinc-950/95 backdrop-blur-xl transition-transform ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`} 
        data-testid="sidebar"
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="border-b border-zinc-800">
            <div className="flex items-center gap-3 p-6">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg flex items-center justify-center shadow-md shadow-emerald-500/20">
                <MessageSquare className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
              <span className="text-xl font-bold text-white tracking-tight" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>MeuGestor</span>
            </div>
            
            {/* WhatsApp Status Indicator */}
            {whatsappConnected !== null && (
              <div className="px-6 pb-2">
                <div 
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${
                    whatsappConnected 
                      ? 'bg-emerald-900/20 text-emerald-400 border border-emerald-700/30' 
                      : 'bg-orange-900/20 text-orange-400 border border-orange-700/30'
                  }`}
                >
                  {whatsappConnected ? (
                    <>
                      <Wifi className="w-3.5 h-3.5" />
                      <span className="font-medium">WhatsApp Conectado</span>
                    </>
                  ) : (
                    <>
                      <WifiOff className="w-3.5 h-3.5" />
                      <span className="font-medium">WhatsApp Desconectado</span>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Subscription Status Indicator */}
            {subscriptionInfo && (
              <div className="px-6 pb-4">
                <div 
                  className={`px-3 py-2 rounded-lg text-xs border ${
                    subscriptionInfo.days_remaining > 7
                      ? 'bg-blue-900/20 text-blue-400 border-blue-700/30' 
                      : subscriptionInfo.days_remaining > 0
                        ? 'bg-amber-900/20 text-amber-400 border-amber-700/30'
                        : 'bg-red-900/20 text-red-400 border-red-700/30'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-3.5 h-3.5" />
                    <span className="font-medium">
                      {subscriptionInfo.days_remaining > 0 
                        ? `${subscriptionInfo.days_remaining} dias restantes`
                        : 'Assinatura expirada'
                      }
                    </span>
                  </div>
                  <button
                    onClick={handleRenewSubscription}
                    className={`w-full flex items-center justify-center gap-1.5 py-1.5 rounded text-xs font-medium transition-colors ${
                      subscriptionInfo.days_remaining > 7
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : subscriptionInfo.days_remaining > 0
                          ? 'bg-amber-600 hover:bg-amber-700 text-white'
                          : 'bg-red-600 hover:bg-red-700 text-white'
                    }`}
                    data-testid="btn-renew-subscription"
                  >
                    <RefreshCw className="w-3 h-3" />
                    Renovar
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Menu Items */}
          <nav className="flex-1 overflow-y-auto p-4">
            <ul className="space-y-2">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path || 
                  (item.hasSubmenu && location.pathname.startsWith(item.path));
                
                // Render item with submenu (Templates)
                if (item.hasSubmenu) {
                  return (
                    <li key={item.path}>
                      <button
                        onClick={() => setTemplatesExpanded(!templatesExpanded)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                          isActive
                            ? 'bg-violet-600 text-white'
                            : 'text-slate-400 hover:bg-zinc-800 hover:text-white'
                        }`}
                        data-testid={item.testId}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="font-medium flex-1 text-left">{item.label}</span>
                        {templatesExpanded ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </button>
                      
                      {/* Submenu */}
                      {templatesExpanded && (
                        <ul className="mt-1 ml-4 space-y-1">
                          {/* Ver todos */}
                          <li>
                            <button
                              onClick={() => handleNavigate('/templates')}
                              className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
                                location.pathname === '/templates' && !location.search
                                  ? 'bg-violet-500/20 text-violet-400'
                                  : 'text-slate-400 hover:bg-zinc-800 hover:text-white'
                              }`}
                              data-testid="nav-templates-all"
                            >
                              <FileText className="w-4 h-4" />
                              <span>Todos</span>
                            </button>
                          </li>
                          
                          {/* Grupos */}
                          {groups.length > 0 && groups.map((group) => (
                            <li key={group.id}>
                              <button
                                onClick={() => handleTemplateGroupClick(group.id)}
                                className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
                                  location.search === `?group=${group.id}`
                                    ? 'bg-violet-500/20 text-violet-400'
                                    : 'text-slate-400 hover:bg-zinc-800 hover:text-white'
                                }`}
                                data-testid={`nav-templates-group-${group.id}`}
                              >
                                <div 
                                  className="w-3 h-3 rounded-full" 
                                  style={{ backgroundColor: group.color || '#6366f1' }}
                                />
                                <span className="truncate">{group.name}</span>
                              </button>
                            </li>
                          ))}
                          
                          {groups.length === 0 && (
                            <li className="px-4 py-2 text-xs text-slate-500">
                              Nenhum grupo cadastrado
                            </li>
                          )}
                        </ul>
                      )}
                    </li>
                  );
                }
                
                // Regular menu item
                return (
                  <li key={item.path}>
                    <button
                      onClick={() => handleNavigate(item.path)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-violet-600 text-white'
                          : 'text-slate-400 hover:bg-zinc-800 hover:text-white'
                      }`}
                      data-testid={item.testId}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Theme Toggle & Logout */}
          <div className="border-t border-zinc-800 p-4 space-y-2">
            <div className="flex items-center gap-2 px-4">
              <span className="text-sm text-slate-400 flex-1">Tema</span>
              <ThemeToggle />
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-zinc-800 hover:text-white transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Sair</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
